import React, { memo, useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import classNames from "classnames";
import { differenceInCalendarDays, parseISO, format } from "date-fns";

import { checkViewerRole } from "common/checkers/viewerChecker";
import styles from "./upcomingTask.module.scss";
import {
  Switch,
  CheckBox,
  CheckType,
  State,
  UserInline,
  UserInlineTypes,
  Button,
  BtnType,
  Pannel,
} from "../../../../primitives";
import { HeaderOptions } from "./upcomingTask.constatnt";
import {
  projectTaskListGet,
  projectTasksByUserGet,
  projectTaskUpdate,
} from "../../tasks/tasks.action";
import { useModelPopup } from "../../../../../common/hooks";
import TaskNewComponent from "../../tasks/taskNew";
import { appSliderSettingSet } from "application/app.action";
import { sliderSetting } from "application/app.constants";
import {
  TaskInfoComponentV2,
  TaskInfoHeaderComponentV2,
} from "../../tasks/taskInfo";
import { projectGet } from "../../project.action";
import fromNow from "../../../../../common/fromNow";
import { isFerminichCouncil } from "../../isFerminichCouncil";
import ApprovalTaskHeader from "modules/projects/project/tasks/approvalTask/approvalTaskHeader.component";
import ApprovalTaskBody from "modules/projects/project/tasks/approvalTask/approvalTaskBody.component";
const UpcomingTasksComponent = ({ canManageTasks }) => {
  const {
    authReducer: { session },
    councilReducer: { selectedCouncil },
    projectsReducer: {
      projectReducer: { selectedProject },
      taskReducer: { tasksList, userTasks, taskCompany },
      companyReducer: { updateTasks },
    },
  } = useSelector((state) => state);
  const dispatch = useDispatch();
  const history = useHistory();
  const [hasLoadMore, setHasLoadMore] = useState(false);
  const [selectedTab, setSelectedTab] = useState(HeaderOptions[0].value);
  const [tasksByTab, setTasksByTab] = useState([]);
  const [patientOrg, setPatientOrg] = useState(false);
  const popup = useModelPopup();
  const { enqueueSnackbar } = useSnackbar();
  const [blockFunctionality, setBlockFunctionality] = useState(false);

  const checkIfFirmenich = (selectedCouncil, selectedProject, type) => {
    if (
      selectedCouncil?.name === "Firmenich" &&
      selectedProject?.stage_template?.default_template
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (checkViewerRole()) {
      setBlockFunctionality(true);
    }
  }, []);

  useEffect(() => {
    if (selectedCouncil && selectedProject) {
      let obj = {
        projectId: selectedProject.id,
        councilId: selectedCouncil.id,
        enqueueSnackbar,
      };
      if (selectedProject?.stage_template?.patient_organization) {
        setPatientOrg(true);
      }
      if (selectedTab === "my_task" && !sliderSetting.show) {
        obj = { ...obj, userId: session.id };
        dispatch(projectTasksByUserGet(obj));
      }
      if (selectedTab === "all_task" && !sliderSetting.show) {
        dispatch(projectTaskListGet(obj));
      }
    }
  }, [selectedTab, selectedProject, selectedCouncil, taskCompany]);

  useEffect(() => {
    if (selectedTab === "all_task" && tasksList) {
      const newData = tasksList.reduce(
        (acc, elem) => {
          const filteredAssignments = elem.projects_task_assignments
            .filter((a) => {
              if (
                checkIfFirmenich(selectedCouncil, selectedProject) &&
                a.projects_task_organization !== null
              ) {
                return false;
              }
              return !a.completed_on;
            })
            .slice(0, 4);

          if (acc.total === 0) {
            return {
              total: filteredAssignments.length,
              data: [
                ...acc.data,
                {
                  ...elem,
                  projects_task_assignments: filteredAssignments,
                },
              ],
            };
          }

          acc.total += filteredAssignments.length;

          if (acc.total > 5) {
            return acc;
          }

          return {
            ...acc,
            data: [
              ...acc.data,
              {
                ...elem,
                projects_task_assignments: filteredAssignments,
              },
            ],
          };
        },
        {
          data: [],
          total: 0,
        }
      );

      setTasksByTab(newData.data);
      setHasLoadMore(newData.total > 5);
    }

    if (selectedTab === "my_task" && userTasks) {
      if (userTasks.length === 0) {
        handleSwitchChange("all_task");
        return;
      }

      const newData = userTasks.reduce(
        (acc, elem) => {
          const filteredAssignments = elem.projects_task_assignments
            .filter((a) => {
              if (
                checkIfFirmenich(selectedCouncil, selectedProject) &&
                a.projects_task_organization !== null
              ) {
                return false;
              }
              return !a.completed_on && a.assigned_to_id === session.id;
            })
            .slice(0, 4);

          if (acc.total === 0) {
            return {
              total: filteredAssignments.length,
              data: [
                ...acc.data,
                {
                  ...elem,
                  user_assignments: filteredAssignments,
                },
              ],
            };
          }

          acc.total += filteredAssignments.length;

          if (acc.total > 5) {
            return acc;
          }

          return {
            ...acc,
            data: [
              ...acc.data,
              {
                ...elem,
                user_assignments: filteredAssignments,
              },
            ],
          };
        },
        {
          data: [],
          total: 0,
        }
      );

      setTasksByTab(newData.data);
      setHasLoadMore(newData.total > 5);
    }

    return () => null;
  }, [selectedTab, tasksList, userTasks]);

  useEffect(() => {
    if (updateTasks && selectedProject) {
      dispatch(projectGet({ id: selectedProject.id, enqueueSnackbar }));
    }
  }, [updateTasks]);

  const handleSwitchChange = (value) => {
    setSelectedTab(value);
    setTasksByTab(null);
    setHasLoadMore(false);
  };

  const handleOnChange = (value, taskId, assignmentId) => {
    dispatch(
      projectTaskUpdate({
        enqueueSnackbar,
        taskId,
        flag: value ? "completed_true" : "completed_false",
        projectId: selectedProject.id,
        project_task: {
          task_assignments: {
            completed_on: value ? format(new Date(), "yyyy-MM-dd") : null,
          },
        },
        queryParams: {
          task_assignment_id: assignmentId,
        },
      })
    );
  };

  const handleAddNewTaskClick = () => {
    popup.show({
      title: "Add a task",
      component: <TaskNewComponent currentPage={1} activeTab={selectedTab} />,
    });
  };

  const getRightPanel = useCallback(
    () => (
      <>
        {checkIfFirmenich(selectedCouncil, selectedProject) ? null : (
          <Switch
            tabs={HeaderOptions}
            onChange={handleSwitchChange}
            selected={selectedTab}
          />
        )}
        {canManageTasks && !blockFunctionality && (
          <Button
            className={styles.newTask}
            btn={BtnType.REGULAR}
            icon="icn-add"
            onClick={handleAddNewTaskClick}
          >
            {isFerminichCouncil("New task", "New Milestone", selectedCouncil)}
          </Button>
        )}
      </>
    ),
    [selectedTab]
  );

  const handleShowTask = (task, assignment) => {
    if (task.locked) {
      return;
    }

    if (task.task_type === 'approval_gate') {
      dispatch(
        appSliderSettingSet({
          ...sliderSetting,
          show: true,
          height: "300",
          width: "540",
          header: (
            <ApprovalTaskHeader task={task} />
          ),
          component: (
            <ApprovalTaskBody
              taskId={task.id}
              blockFunctionality={selectedCouncil?.name === 'Ford' ? canManageTasks : blockFunctionality}
            />
          ),
        })
      );
      return
    }

    dispatch(
      appSliderSettingSet({
        ...sliderSetting,
        show: true,
        height: "300",
        width: "540",
        header: (
          <TaskInfoHeaderComponentV2
            taskId={task.id}
            projectId={task.project_id}
            assignmentId={assignment.id}
            completed={assignment.completed}
          />
        ),
        component: <TaskInfoComponentV2
          task={task}
          assignment={assignment}
          blockFunctionality={selectedCouncil?.name === 'Ford' ? canManageTasks : blockFunctionality}
        />,
      })
    );
  };

  const goTo = (evt) => {
    evt.preventDefault();
    history.push(`/projects/${selectedProject.id}/task`);
    window.location.reload();
  };

  const makeDueDateStyles = useCallback(
    (assignment) => {
      if (!assignment.due_date) return "";

      const diffInDays = differenceInCalendarDays(
        parseISO(assignment.due_date),
        new Date()
      );

      if (diffInDays < 0) {
        return styles.expired;
      }

      if (diffInDays <= 3 && diffInDays >= 0) {
        return styles.progress;
      }

      return "";
    },
    [tasksByTab]
  );

  return (
    <Pannel
      title={isFerminichCouncil(
        "Upcoming Tasks",
        "Upcoming Milestones",
        selectedCouncil
      )}
      padding="0"
      rightContent={getRightPanel()}
    >
      <div className={styles.wrapper}>
        <div className={styles.grid}>
          <div className={styles.gridHeader}>
            <div className={styles.select} />
            <div className={styles.company}>
              {checkIfFirmenich(selectedCouncil, selectedProject) ? (
                <></>
              ) : (
                <> {patientOrg ? "Organization" : "Company"}</>
              )}
            </div>
            <div className={styles.task}>
              {checkIfFirmenich(selectedCouncil, selectedProject)
                ? "NEXT MILESTONE"
                : "Task"}
            </div>
            {checkIfFirmenich(selectedCouncil, selectedProject) ? (
              <></>
            ) : (
              <div className={styles.stage}>Stage</div>
            )}

            <div className={styles.due}>Due</div>
            <div className={styles.assigned}>Assigned</div>
          </div>
          <div className={styles.gridBody}>
            {selectedTab === "my_task" &&
              (tasksByTab ? (
                tasksByTab.length ? (
                  tasksByTab.map((task) =>
                    task.user_assignments
                      .filter((assignment) => !assignment?.completed_on)
                      .map((assignment) => (
                        <div
                          key={assignment.id}
                          className={classNames(
                            styles.gridRow,
                            task.locked ? styles.lockedTask : ''
                          )}
                          onClick={() => handleShowTask(task, assignment)}
                        >
                          <div className={styles.select}>
                            {!blockFunctionality && (
                              <CheckBox
                                readOnly={task.locked ? true : selectedCouncil?.name === 'Ford' ? canManageTasks : false}
                                isChecked={assignment.completed_on}
                                checkType={CheckType.BLUE}
                                onChange={(value) =>
                                  handleOnChange(value, task.id, assignment?.id)
                                }
                              />
                            )}
                          </div>
                          <div className={styles.company}>
                            {assignment.projects_task_organization
                              ? assignment.projects_task_organization
                                  .organization?.name
                              : isFerminichCouncil(
                                  "Project Wide",
                                  "Milestone",
                                  selectedCouncil
                                )}
                          </div>
                          <div className={styles.task}>{task.name}</div>
                          {checkIfFirmenich(
                            selectedCouncil,
                            selectedProject
                          ) ? (
                            <></>
                          ) : (
                            <div className={styles.stage}>
                              {task.stage_id && task.stage && (
                                <State stageId={task.stage_id} />
                              )}
                            </div>
                          )}

                          <div
                            className={classNames(
                              styles.due,
                              makeDueDateStyles(assignment)
                            )}
                          >
                            {assignment.due_date
                              ? fromNow(
                                  differenceInCalendarDays(
                                    parseISO(assignment.due_date),
                                    new Date()
                                  )
                                )
                              : "-"}
                          </div>
                          <div className={styles.assigned}>
                            <UserInline
                              userId={assignment?.assigned_to?.id}
                              avatar={assignment?.assigned_to?.avatar}
                              type={UserInlineTypes.WITH_NAME}
                              noNavigation
                            />
                          </div>
                        </div>
                      ))
                  )
                ) : (
                  <p className="text-center mt-4">No data.</p>
                )
              ) : (
                <p className="text-center mt-4">Loading...</p>
              ))}
            {selectedTab === "all_task" &&
              (tasksByTab ? (
                tasksByTab.length ? (
                  tasksByTab.map((task) =>
                    task.projects_task_assignments
                      .filter((assignment) => !assignment?.completed_on)
                      .map((assignment) => (
                        <div
                          key={assignment.id}
                          className={classNames(
                            styles.gridRow,
                            task.locked ? styles.lockedTask : ''
                          )}
                          onClick={() => handleShowTask(task, assignment)}
                        >
                          <div className={styles.select}>
                            {!blockFunctionality && (
                              <CheckBox
                                readOnly={task.locked ? true : selectedCouncil?.name === 'Ford' ? canManageTasks : false}
                                isChecked={assignment.completed_on}
                                checkType={CheckType.BLUE}
                                onChange={(value) =>
                                  handleOnChange(value, task.id, assignment?.id)
                                }
                              />
                            )}
                          </div>
                          <div className={styles.company}>
                            {assignment.projects_task_organization
                              ? assignment.projects_task_organization
                                  .organization?.name
                              : isFerminichCouncil(
                                  "Project Wide",
                                  "Milestone",
                                  selectedCouncil
                                )}
                          </div>
                          <div className={styles.task}>{task.name}</div>
                          {checkIfFirmenich(
                            selectedCouncil,
                            selectedProject
                          ) ? (
                            <></>
                          ) : (
                            <div className={styles.stage}>
                              {task.stage_id && task.stage && (
                                <State stageId={task.stage_id} />
                              )}
                            </div>
                          )}

                          <div
                            className={classNames(
                              styles.due,
                              makeDueDateStyles(assignment)
                            )}
                          >
                            {assignment.due_date_in_words || "-"}
                          </div>
                          <div className={styles.assigned}>
                            <UserInline
                              userId={assignment?.assigned_to?.id}
                              avatar={assignment?.assigned_to?.avatar}
                              type={UserInlineTypes.WITH_NAME}
                              noNavigation
                            />
                          </div>
                        </div>
                      ))
                  )
                ) : (
                  <p className="text-center mt-4">No data.</p>
                )
              ) : (
                <p className="text-center mt-4">Loading...</p>
              ))}
            {hasLoadMore ? (
              <div className={styles.showAll}>
                <Link onClick={goTo} to="./task">
                  Show all tasks
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Pannel>
  );
};

export default memo(UpcomingTasksComponent);
